/**
 * API модуль для взаимодействия с каталогом курсов
 */

import {
  CoursesQueryParams,
  FilterParametersResponse,
  ElasticSearchResponse,
} from "../types/api"
import { Course } from "../types/course"
import { Subject, Teacher, Class, Level } from "../types/filter"
import { createLogger, LogLevel } from "../utils/logger"

// Создаем логгер для API модуля
const logger = createLogger("API", LogLevel.INFO)

/**
 * Класс ошибки API
 */
export class ApiError extends Error {
  status?: number

  constructor(message: string, status?: number) {
    super(message)
    this.name = "ApiError"
    this.status = status
  }
}

/**
 * Класс для работы с API каталога курсов
 */
class CoursesApi {
  /**
   * Получить агрегации (дисциплины, эксперты, классы, цели) с size: 0
   */
  async getCustomAggregations(): Promise<any> {
    const body = {
      from: 0,
      size: 0,
      sort: [
        { is_ready_to_buy: "desc" },
        {
          "goal.id": {
            order: "asc",
            nested: { path: "goal" },
          },
        },
        { duration: "desc" },
        {
          "classes.grade": {
            order: "desc",
            nested: { path: "classes" },
          },
        },
        { course_priority: "asc" },
        { _id: "asc" },
      ],
      query: {
        bool: {
          must: [{ term: { collections: 1 } }],
        },
      },
      aggs: {
        discipline_names: {
          nested: { path: "discipline" },
          aggs: {
            names: {
              terms: { field: "discipline.name.keyword", size: 100 },
            },
          },
        },
        expert_names: {
          nested: { path: "experts" },
          aggs: {
            names: {
              terms: { field: "experts.name.keyword", size: 100 },
            },
          },
        },
        class_grades: {
          nested: { path: "classes" },
          aggs: {
            grades: { terms: { field: "classes.grade" } },
          },
        },
        goal: {
          nested: { path: "goal" },
          aggs: {
            goal: { terms: { field: "goal.slug" } },
          },
        },
      },
    }
    logger.info("ElasticSearch: кастомный запрос агрегаций:", body)
    const response = await this.post<any>(body)
    logger.info("ElasticSearch: ответ на кастомные агрегации:", response)
    return response.aggregations
  }
  private apiUrl: string
  private timeout: number = 15000

  /**
   * Конструктор класса
   * @param apiUrl URL для API запросов (опционально)
   */
  constructor(apiUrl?: string) {
    if (apiUrl) {
      this.apiUrl = apiUrl;
    } else {
      const currentDomain = window.location.hostname;
      const indexName = currentDomain === 'dev-wp.100points.ru' ? 'preprod_100points_courses' : 'prod_100points_courses';
      this.apiUrl = `https://search.100points.ru/${indexName}/_search`;
    }
    logger.info("API инициализирован с URL:", this.apiUrl)
  }

  /**
   * Универсальный POST-запрос к ElasticSearch API
   */
  private async post<T>(body: object): Promise<T> {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), this.timeout)
    try {
      const response = await fetch(this.apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      })
      if (!response.ok) {
        const errorText = await response.text().catch(() => "")
        logger.error(`Ошибка API (${response.status}): ${errorText}`)
        throw new ApiError(
          `Ошибка API: ${response.status} ${response.statusText}`,
          response.status
        )
      }
      return await response.json()
    } catch (error) {
      if (error instanceof ApiError) throw error
      logger.error("Ошибка сети или парсинга:", error)
      throw new ApiError(
        error instanceof Error ? error.message : "Неизвестная ошибка"
      )
    } finally {
      clearTimeout(timeoutId)
    }
  }

  /**
   * Генерация тела запроса для поиска курсов (ElasticSearch DSL)
   */
  private buildCoursesQuery(params: CoursesQueryParams = {}): object {
    const {
      subject = "",
      collection = 1,
      class: classGrade,
      goal,
      from = 0,
      size = 6,
    } = params as CoursesQueryParams & {
      from?: number
      size?: number
      collection?: number
    }
    const filter: any[] = [{ term: { collections: collection } }]
    if (subject) {
      filter.push({
        nested: {
          path: "discipline",
          query: {
            bool: {
              filter: [{ term: { "discipline.name.keyword": subject } }],
            },
          },
        },
      })
    }
    if (classGrade !== undefined && classGrade !== null) {
      filter.push({
        nested: {
          path: "classes",
          query: {
            bool: {
              filter: [{ term: { "classes.grade": classGrade } }],
            },
          },
        },
      })
    }
    if (goal) {
      filter.push({
        nested: {
          path: "goal",
          query: {
            bool: {
              filter: [{ term: { "goal.slug": goal } }],
            },
          },
        },
      })
    }
    if (params.teacher) {
      filter.push({
        nested: {
          path: "experts",
          query: {
            bool: {
              filter: [{ term: { "experts.name.keyword": params.teacher } }],
            },
          },
        },
      })
    }
    return {
      from,
      size,
      sort: [
        { is_ready_to_buy: "desc" },
        {
          "goal.id": {
            order: "asc",
            nested: { path: "goal" },
          },
        },
        { duration: "desc" },
        {
          "classes.grade": {
            order: "desc",
            nested: { path: "classes" },
          },
        },
        { course_priority: "asc" },
        { _id: "asc" },
      ],
      query: { bool: { filter } },
      aggs: {
        discipline_names: {
          nested: { path: "discipline" },
          aggs: {
            names: { terms: { field: "discipline.name.keyword", size: 100 } },
          },
        },
        expert_names: {
          nested: { path: "experts" },
          aggs: {
            names: { terms: { field: "experts.name.keyword", size: 100 } },
          },
        },
        course_priority: {
          terms: {
            field: "course.priority",
          },
        },
        class_grades: {
          nested: { path: "classes" },
          aggs: {
            grades: { terms: { field: "classes.grade" } },
          },
        },
        goal: {
          nested: { path: "goal" },
          aggs: {
            goal: { terms: { field: "goal.slug" } },
          },
        },
      },
    }
  }

  /**
   * Получить список курсов с фильтрацией
   */
  async getCourses(
    params: CoursesQueryParams = {}
  ): Promise<{ data: any[]; meta: any }> {
    logger.info("ElasticSearch: запрос курсов с параметрами:", params)
    const query = this.buildCoursesQuery(params)
    const esResponse = await this.post<any>(query)
    logger.info("ElasticSearch: ответ от API:", esResponse)
    // Возвращаем массив _source без нормализации
    const total =
      typeof esResponse.hits.total === "object"
        ? esResponse.hits.total.value
        : esResponse.hits.total
    const perPage = (params as any).size || 6
    const currentPage = (params as any).from
      ? Math.floor((params as any).from / perPage) + 1
      : 1
    return {
      data: esResponse.hits.hits.map((hit: any) => hit._source),
      meta: {
        current_page: currentPage,
        total_pages: Math.ceil(total / perPage),
        total,
        per_page: perPage,
      },
    }
  }

  /**
   * Получить параметры фильтрации (агрегации) с учётом фильтров
   */
  async getFilterParameters(
    params: CoursesQueryParams = {}
  ): Promise<FilterParametersResponse> {
    logger.info(
      "ElasticSearch: запрос параметров фильтрации с параметрами:",
      params
    )
    const query = this.buildCoursesQuery({ ...params, size: 0 })
    const esResponse = await this.post<ElasticSearchResponse>(query)
    return this.normalizeFilters(esResponse)
  }

  /**
   * Нормализация фильтров из ElasticSearch-агрегаций
   */
  private normalizeFilters(
    es: ElasticSearchResponse
  ): FilterParametersResponse {
    const aggs = es.aggregations || {}
    const mapSubjects = (buckets: any[] = []): Subject[] =>
      buckets.map((b: any) => ({ name: String(b.key), slug: String(b.key) }))
    const mapTeachers = (buckets: any[] = []): Teacher[] =>
      buckets.map((b: any) => ({ name: String(b.key), slug: String(b.key) }))
    const mapClasses = (buckets: any[] = []): Class[] =>
      buckets.map((b: any) => ({ name: String(b.key), slug: String(b.key) }))
    const mapLevels = (buckets: any[] = []): Level[] =>
      buckets.map((b: any) => ({
        name: String(b.key),
        slug: String(b.key),
        goal: String(b.key),
      }))
    return {
      subjects: mapSubjects(aggs.discipline_names?.names?.buckets),
      levels: mapLevels(aggs.goals?.goals?.buckets),
      classes: mapClasses(aggs.class_grades?.grades?.buckets),
      teachers: mapTeachers(aggs.expert_names?.names?.buckets),
    }
  }

  /**
   * Получить общее количество курсов с учётом фильтров
   */
  async getTotalCoursesCount(params: CoursesQueryParams = {}): Promise<number> {
    const query = this.buildCoursesQuery({ ...params, size: 0 })
    const esResponse = await this.post<any>(query)
    return typeof esResponse.hits.total === "object"
      ? esResponse.hits.total.value
      : esResponse.hits.total
  }

  /**
   * Получить сырые aggregations из ElasticSearch (raw-ответ)
   */
  async getRawAggregations(params: CoursesQueryParams = {}): Promise<any> {
    logger.info(
      "ElasticSearch: запрос сырых aggregations с параметрами:",
      params
    )
    const query = this.buildCoursesQuery({ ...params, size: 0 })
    const esResponse = await this.post<any>(query)
    return esResponse.aggregations
  }
}

// Экспортируем синглтон для использования в приложении
const coursesApi = new CoursesApi()
export default coursesApi
